import React, { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LogOut, GraduationCap, Plane, MessagesSquare, Sparkles } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

export default function StudentPortal() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/portal/student").then(r => setData(r.data));
  }, []);

  return (
    <div className="min-h-screen bg-[var(--miro-bg)]">
      <header className="bg-white border-b border-[var(--miro-border)]">
        <div className="max-w-5xl mx-auto flex items-center gap-3 px-6 py-4">
          <div className="w-10 h-10 rounded-lg bg-white border border-[var(--miro-border)] p-1">
            <img src="/miro-logo.png" alt="MIRO" className="w-full h-full object-contain"/>
          </div>
          <div>
            <div className="font-display font-black text-lg text-[var(--miro-navy)] tracking-tight">MIRO Student Portal</div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--miro-text-muted)]">Your journey, in one place</div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold text-[var(--miro-navy)]">{user?.name}</div>
              <div className="text-[11px] text-[var(--miro-text-muted)]">Student · client</div>
            </div>
            <Button data-testid="portal-logout" variant="outline" size="sm" onClick={logout}>
              <LogOut size={14} className="mr-1"/> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        <div className="miro-card p-6 mb-6" data-testid="portal-hero"
             style={{ background: "linear-gradient(120deg,#041B46 0%,#0A2657 100%)", color: "#fff" }}>
          <div className="miro-overline text-[var(--miro-blue)]">Welcome back</div>
          <h1 className="font-display font-black text-3xl tracking-tight mt-2">
            Hi {user?.name?.split(" ")[0]} 👋
          </h1>
          <p className="text-sm text-white/70 mt-2 max-w-lg">
            Track your enrolments, applications and MIRO team conversations here. If you have questions, your counsellor is one message away.
          </p>
        </div>

        <Tabs defaultValue="enrolments">
          <TabsList data-testid="portal-tabs" className="bg-white border border-[var(--miro-border)]">
            <TabsTrigger value="enrolments" data-testid="portal-tab-enrolments">Enrolments</TabsTrigger>
            <TabsTrigger value="applications" data-testid="portal-tab-applications">Applications</TabsTrigger>
            <TabsTrigger value="messages" data-testid="portal-tab-messages">Messages</TabsTrigger>
          </TabsList>

          <TabsContent value="enrolments" className="mt-4">
            <div className="space-y-3">
              {(data?.enrolments || []).length === 0 && <Empty msg="No enrolments yet." icon={GraduationCap}/>}
              {(data?.enrolments || []).map(e => (
                <div key={e.id} data-testid={`portal-enrol-${e.id}`} className="miro-card p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: "#0EA5FF18", color: "#0284C7" }}>
                      <GraduationCap size={19}/>
                    </div>
                    <div className="flex-1">
                      <div className="font-display font-bold text-[var(--miro-navy)]">{e.student_name}</div>
                      <div className="text-xs text-[var(--miro-text-muted)]">Program ID: {e.program_id.slice(0, 8)}…</div>
                    </div>
                    <Badge className="border-0 font-semibold capitalize" style={{ background: "#DCFCE7", color: "#15803D" }}>
                      {e.status.replace(/_/g," ")}
                    </Badge>
                  </div>
                  {e.placement_company && (
                    <div className="mt-3 text-sm text-[var(--miro-text-secondary)]">
                      Placed at <span className="font-semibold text-[var(--miro-navy)]">{e.placement_company}</span>
                      {e.placement_role && ` as ${e.placement_role}`}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="applications" className="mt-4">
            <div className="space-y-3">
              {(data?.applications || []).length === 0 && <Empty msg="No applications yet." icon={Plane}/>}
              {(data?.applications || []).map(a => (
                <div key={a.id} data-testid={`portal-app-${a.id}`} className="miro-card p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: "#5FBF3A18", color: "#15803D" }}>
                      <Plane size={19}/>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-[var(--miro-navy)]">{a.university}</div>
                      <div className="text-xs text-[var(--miro-text-muted)] truncate">{a.country} · {a.program} · {a.intake_season} {a.intake_year}</div>
                    </div>
                    <Badge className="border-0 font-semibold capitalize" style={{ background: "#DBEAFE", color: "#1D4ED8" }}>{a.status.replace(/_/g," ")}</Badge>
                  </div>
                  {a.docs && (
                    <div className="mt-3">
                      <div className="flex justify-between text-[11px] text-[var(--miro-text-muted)] mb-1">
                        <span>Documents</span>
                        <span>{a.docs.filter(d=>d.done).length}/{a.docs.length}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-[var(--miro-bg)] overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${a.docs.length ? (a.docs.filter(d=>d.done).length / a.docs.length) * 100 : 0}%`, background: "linear-gradient(90deg,#0EA5FF,#5FBF3A)" }}/>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="messages" className="mt-4">
            <div className="space-y-3">
              {(data?.communications || []).length === 0 && <Empty msg="No messages yet." icon={MessagesSquare}/>}
              {(data?.communications || []).map(c => (
                <div key={c.id} data-testid={`portal-comm-${c.id}`} className="miro-card p-4">
                  <div className="flex items-center gap-2 text-[11px] text-[var(--miro-text-muted)]">
                    <span className="uppercase tracking-wider font-semibold">{c.type}</span>
                    <span>·</span>
                    <span>{formatDistanceToNow(new Date(c.created_at), { addSuffix: true })}</span>
                  </div>
                  {c.subject && <div className="font-semibold text-sm text-[var(--miro-navy)] mt-1">{c.subject}</div>}
                  <div className="text-sm text-[var(--miro-text-secondary)] whitespace-pre-wrap mt-1">{c.body}</div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-8 text-center text-xs text-[var(--miro-text-muted)]">
          <Sparkles size={12} className="inline mr-1"/> Powered by MIRO One · Every conversation, every step, in one place.
        </div>
      </main>
    </div>
  );
}

function Empty({ msg, icon: Icon }) {
  return (
    <div className="miro-card p-10 text-center">
      <Icon size={30} className="mx-auto text-[var(--miro-text-muted)]"/>
      <div className="text-sm text-[var(--miro-text-muted)] mt-3">{msg}</div>
    </div>
  );
}
